import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Card,
  CardContent,
  CardActions,
  Typography,
  Stack,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import closeFill from "@iconify/icons-eva/close-fill";
import * as xlsx from "xlsx";
import orderService from "../services/orderService";

const MAX_IMPORT_ROWS = 500;

interface UploadOrderModalProps {
  open: boolean;
  handler: () => void;
  onSuccess: () => void;
}

export default function UploadOrderModal(props: UploadOrderModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<any[]>([]);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    if (props.open) {
      setFile(null);
      setErrors([]);
      setSuccessMsg("");
      setSubmitting(false);
    }
  }, [props.open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setErrors([]);
      setSuccessMsg("");
    }
  };

  const resetForm = () => {
    setFile(null);
    setErrors([]);
    setSuccessMsg("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    // Resetar o arquivo IMEDIATAMENTE ao clicar enviar
    const fileToUpload = file;
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setSubmitting(true);
    setErrors([]);
    setSuccessMsg("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        const workbook = xlsx.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = xlsx.utils.sheet_to_json(worksheet);

        if (!jsonData || jsonData.length === 0) {
          setErrors([{ message: "A planilha está vazia ou no formato incorreto." }]);
          setSubmitting(false);
          return;
        }

        if (jsonData.length > MAX_IMPORT_ROWS) {
          setErrors([
            {
              message: `A planilha possui ${jsonData.length} ordens. Envie no máximo ${MAX_IMPORT_ROWS} ordens por importação.`,
            },
          ]);
          setSubmitting(false);
          return;
        }

        try {
          const res = await orderService.insertBatch(jsonData);
          setSuccessMsg(`✅ Sucesso! ${res.data.successCount} OS(s) foram importadas com êxito.`);
          props.onSuccess();
        } catch (apiError: any) {
          if (apiError.response && apiError.response.data && apiError.response.data.errors) {
            setErrors(apiError.response.data.errors);
          } else {
            setErrors([{ message: "Erro de conexão com o servidor ao processar planilha." }]);
          }
        }
      } catch (err) {
        setErrors([{ message: "Erro ao ler arquivo da planilha." }]);
      } finally {
        setSubmitting(false);
      }
    };
    reader.readAsBinaryString(fileToUpload);
  };

  return (
    <Modal
      open={props.open}
      onClose={() => {
        if (!isSubmitting) props.handler();
      }}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Card sx={{ minWidth: "40vw", maxHeight: "90vh", overflowY: "auto" }}>
        <CardContent>
          <Typography variant="h4" marginBottom={4} color="#004A78" gutterBottom>
            Importar OS via Planilha
          </Typography>
          <Stack spacing={2} marginTop={3}>
            <Typography variant="body1">
              Selecione o arquivo XLSX preenchido com as OSs. Limite de 500 ordens por importação.
            </Typography>
            <Button variant="outlined" component="label" sx={{ width: "fit-content" }}>
              Selecionar Arquivo
              <input type="file" ref={fileInputRef} hidden accept=".xlsx,.csv" onChange={handleFileChange} />
            </Button>
            {file && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">Arquivo: {file.name}</Typography>
                <IconButton
                  size="small"
                  onClick={handleClearFile}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      bgcolor: "#f0f0f0",
                    },
                  }}
                >
                  <Icon icon={closeFill} width="18px" height="18px" />
                </IconButton>
              </Stack>
            )}

            {successMsg && (
              <Typography variant="body1" color="success.main">
                {successMsg}
              </Typography>
            )}

            {errors.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle1" color="error">
                  Ocorreram os seguintes erros (nenhuma OS foi salva):
                </Typography>
                <List dense sx={{ bgcolor: "#ffeeee", borderRadius: "8px", maxHeight: "200px", overflow: "auto" }}>
                  {errors.map((err, i) => (
                    <ListItem key={i}>
                      <ListItemText
                        primary={`Linha ${err.row || '?'}: OS ${err.code_Order || 'Desconhecida'}`}
                        secondary={err.message}
                        primaryTypographyProps={{ color: "error" }}
                        secondaryTypographyProps={{ color: "error" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Stack>
            )}
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end", padding: "16px" }}>
          <Button onClick={props.handler} disabled={isSubmitting} color="inherit">
            {successMsg ? "Fechar" : "Fechar"}
          </Button>
          {successMsg ? (
            <Button
              variant="contained"
              onClick={resetForm}
              sx={{
                backgroundColor: "#004A78",
                color: "#FEFEFE",
                "&:hover": {
                  bgcolor: "#003A68",
                },
              }}
            >
              Enviar Outra Planilha
            </Button>
          ) : (
            <LoadingButton
              variant="contained"
              loading={isSubmitting}
              disabled={!file}
              onClick={handleUpload}
              sx={{
                backgroundColor: "#004A78",
                color: "#FEFEFE",
                "&:hover": {
                  bgcolor: "#003A68",
                },
              }}
            >
              Enviar Planilha
            </LoadingButton>
          )}
        </CardActions>
      </Card>
    </Modal>
  );
}
